import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import config from "../../config/config";
import axios from "axios";
import { message } from "antd";

const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const hoursOfTheDay = Array.from({ length: 24 }, (_, hour) => {
  const ampm = hour < 12 ? "AM" : "PM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const minuteIntervals = ["00", "15", "30", "45"];

  return minuteIntervals.map((minutes) => `${hour12}:${minutes} ${ampm}`);
}).flat();

const CalendarComponent = ({ user, initialDate }) => {
  const [selectedDate, setSelectedDate] = useState(
    new Date(initialDate || new Date())
  );
  const [hour, setHour] = useState(selectedDate.getHours());
  const [duration, setDuration] = useState("");
  const [method, setMethod] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const location = useLocation();
  const baseURL = config.API_BASE_URL;

  const navigate = useNavigate();
  const [messageId, setMessageId] = useState("");
  const [jobId, setJobId] = useState("");
  const [setterId, setSetterId] = useState("");
  const [confirmerId, setConfirmerId] = useState("");

  useEffect(() => {
    console.log("Captured messageId: ", messageId);
  }, [messageId]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const dateString = searchParams.get("date");
    const timeString =
      searchParams.get("time") + " " + searchParams.get("ampm");
    const messageId = searchParams.get("messageId");
    const jobIdParam = searchParams.get("jobId");
    const setterIdParam = searchParams.get("setterId");
    const confirmerIdParam = searchParams.get("confirmerId");

    setMessageId(messageId);
    setJobId(jobIdParam);
    setSetterId(setterIdParam);
    if (confirmerIdParam === user.id) {
      setConfirmerId(user.id);
    }

    if (dateString && timeString) {
      const [year, month, day] = dateString.split("-").map(Number);
      const newDate = new Date(year, month - 1, day);
      setSelectedDate(newDate);

      const formattedTime = timeString.trim().replace(/ /g, " ");
      const timeIndex = hoursOfTheDay.findIndex(
        (time) => time === formattedTime
      );
      if (timeIndex !== -1) {
        setHour(timeIndex);
        const hour24 =
          parseInt(formattedTime.split(":")[0]) +
          (formattedTime.includes("PM") &&
          parseInt(formattedTime.split(":")[0]) !== 12
            ? 12
            : 0);
        newDate.setHours(hour24);
        newDate.setMinutes(parseInt(formattedTime.split(":")[1]));
        setSelectedDate(newDate);
      }
    }

    setDuration(searchParams.get("duration"));
    setMethod(decodeURIComponent(searchParams.get("method")));
    console.log("URL Parameters extracted:", {
      dateString,
      timeString,
      messageId,
      jobId: jobIdParam,
      setterId: setterIdParam,
      confirmerId: confirmerIdParam,
      duration: searchParams.get("duration"),
      method: decodeURIComponent(searchParams.get("method")),
    });
  }, [location.search]);

  const handleDateClick = (day) => {
    if (day) {
      const newDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        day
      );
      setSelectedDate(newDate);
    }
  };

  const handleHourChange = (event) => {
    const index = parseInt(event.target.value, 10);
    const newDate = new Date(selectedDate);
    const selectedTime = hoursOfTheDay[index];
    const hour24 =
      parseInt(selectedTime.split(":")[0]) +
      (selectedTime.includes("PM") &&
      parseInt(selectedTime.split(":")[0]) !== 12
        ? 12
        : 0);

    newDate.setHours(hour24);
    newDate.setMinutes(parseInt(selectedTime.split(":")[1].split(" ")[0]));
    setSelectedDate(newDate);
    setHour(index);
  };

  const handleMonthChange = (offset) => {
    const newDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth() + offset,
      selectedDate.getDate()
    );
    setSelectedDate(newDate);
  };

  const handleYearChange = (offset) => {
    const newDate = new Date(
      selectedDate.getFullYear() + offset,
      selectedDate.getMonth(),
      selectedDate.getDate()
    );
    setSelectedDate(newDate);
  };

  const daysInMonth = new Date(
    selectedDate.getFullYear(),
    selectedDate.getMonth() + 1,
    0
  ).getDate();
  const firstDayIndex = new Date(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    1
  ).getDay();
  const daysArray = Array.from({ length: firstDayIndex }, () => null).concat(
    Array.from({ length: daysInMonth }, (_, i) => i + 1)
  );

  const generateUniqueId = () => {
    const timestamp = Date.now();
    const randomPortion = Math.random().toString(36).substring(2, 15);
    return `${timestamp}-${randomPortion}`;
  };

  const generateSessionName = () => {
    const uniqueId = generateUniqueId();
    const lastHalfSetterId = setterId.slice(-Math.floor(setterId.length / 2));
    const lastHalfMessageId = messageId.slice(
      -Math.floor(messageId.length / 2)
    );
    console.log("Confirmer and User IDs", confirmerId, user.id);

    if (confirmerId !== user.id) {
      throw new Error("Confirmer ID does not match User ID, operation halted.");
    }

    const lastHalfConfirmerId = user.id.slice(-Math.floor(user.id.length / 2));
    return `osh_${lastHalfSetterId}_${lastHalfMessageId}_${lastHalfConfirmerId}_${uniqueId}`;
  };

  const handleSubmit = async () => {
    const dateStr = `${selectedDate.getFullYear()}-${
      selectedDate.getMonth() + 1
    }-${selectedDate.getDate()}`;
    const timeStr = hoursOfTheDay[hour];
    const sessionName = generateSessionName();
    const confirmationText =
      "I am thrilled to inform you that our Meet and Greet session has been confirmed! I look forward to speaking with you. Thank you";

    try {
      await axios.patch(`${baseURL}/v1/messages/confirm-invite/${messageId}`, {
        isMeetAndGreetConfirmed: true,
        meetingConfirmedBy: `${user.userType}`,
        meetAndGreetSessionName: sessionName,
        meetAndGreetDetails: {
          date: dateStr,
          time: timeStr,
          preferredMethod: method,
          duration: duration,
        },
        newMessage: {
          text: confirmationText,
          sender: user.userType,
          receiver: "worker",
          jobId: jobId,
          read: false,
        },
      });

      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        navigate(`/account/fs-dashboard/inbox/${messageId}`);
      }, 5000);
    } catch (error) {
      console.error("Failed to set Meet and Greet:", error);
    }
  };

  return (
    <div className="calendar-widget-container">
      <div className="calendar-section">
        <div className="preview-meeting-details">
          <h3>Meeting Details:</h3>
          <p>Date: {`${selectedDate.toDateString()}`}</p>
          <p>Time: {hoursOfTheDay[hour]}</p>
          <p>Duration: {duration}</p>
          <p>Method: {method}</p>
        </div>
        <div className="scheduler-confirm">
          {showSuccessMessage && (
            <div className="scheduler-success-message">
              Attendance Confirmed Successfully!
            </div>
          )}
          <button onClick={handleSubmit}>Confirm Attendance</button>
        </div>
      </div>
      <div className="calendar-section">
        <h2>{`${selectedDate.toLocaleString("default", {
          month: "long",
        })} ${selectedDate.getFullYear()}`}</h2>
        <div className="scheduler-month-navigation">
          <button onClick={() => handleMonthChange(-1)}>Prev Month</button>
          <button onClick={() => handleMonthChange(1)}>Next Month</button>
          <button onClick={() => handleYearChange(-1)}>Prev Year</button>
          <button onClick={() => handleYearChange(1)}>Next Year</button>
        </div>

        <div className="scheduler-calendar">
          {daysOfWeek.map((day) => (
            <div key={day} className="scheduler-day-header">
              {day}
            </div>
          ))}
          {daysArray.map((day, index) => (
            <div
              key={index}
              className={`scheduler-calendar-day ${
                day === selectedDate.getDate() ? "selected-day" : ""
              }`}
              onClick={() => handleDateClick(day)}
            >
              {day || ""}
            </div>
          ))}
        </div>
        <label htmlFor="time-select">Time :</label>
        <select value={hour} onChange={handleHourChange}>
          {hoursOfTheDay.map((hr, index) => (
            <option key={index} value={index}>
              {hr}
            </option>
          ))}
        </select>
      </div>

      <div className="current-schedules-section">
        <h3>Confirmed Schedules</h3>
        {/* Content for current schedules */}
      </div>
    </div>
  );
};

export default CalendarComponent;
